// SigmaStudioCalculators.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import {
  VictoryChart,
  VictoryBar,
  VictoryLine,
  VictoryAxis,
  VictoryLabel,
  VictoryHistogram,   
  VictoryScatter       
} from "victory";
import './SigmaStudio.css';

const SigmaStudioCalculators = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Form fields
  const [processName, setProcessName]         = useState('');
  const [taskName, setTaskName]         = useState('');
  const [taskDetails, setTaskDetails]   = useState('');
  const [oDetails, setODetails]         = useState('');
  const [dDetails, setDDetails]         = useState('');
  const [finalRemarks, setFinalRemarks] = useState('');

  // Calculation inputs & results
  const [opportunities, setOpportunities] = useState('');
  const [defects, setDefects]             = useState('');
  const [dpmo, setDPMO]                   = useState('');
  const [sigma, setSigma]                 = useState('');

  // UI toggles
  const [showTDetails, setShowTDetails]   = useState(false);
  const [showODetails, setShowODetails]   = useState(false);
  const [showDDetails, setShowDDetails]   = useState(false);
  const [showCard, setShowCard]           = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const normalCDFInverse = (p) => {
    const a1 = -39.6968302866538, a2 = 220.946098424521, a3 = -275.928510446969;
    const a4 = 138.357751867269, a5 = -30.6647980661472, a6 = 2.50662827745924;

    const b1 = -54.4760987982241, b2 = 161.585836858041, b3 = -155.698979859887;
    const b4 = 66.8013118877197, b5 = -13.2806815528857;

    const c1 = -0.00778489400243029, c2 = -0.322396458041136;
    const c3 = -2.40075827716184, c4 = -2.54973253934373;
    const c5 = 4.37466414146497, c6 = 2.93816398269878;

    const d1 = 0.00778469570904146, d2 = 0.32246712907004;
    const d3 = 2.445134137143, d4 = 3.75440866190742;

    const pLow = 0.02425;
    const pHigh = 1 - pLow;
    let q, r;

    if (p < pLow) {
      q = Math.sqrt(-2 * Math.log(p));
      return (
        (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
        ((((d1 * q + d2) * q + d3) * q + d4) * q + 1)
      );
    }

    if (p <= pHigh) {
      q = p - 0.5;
      r = q * q;
      return (
        (((((a1 * r + a2) * r + a3) * r + a4) * r + a5) * r + a6) * q /
        (((((b1 * r + b2) * r + b3) * r + b4) * r + b5) * r + 1)
      );
    }

    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(
      (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
      ((((d1 * q + d2) * q + d3) * q + d4) * q + 1)
    );
  };

  const isValidPositiveInteger = (value) => {
    return /^\d+$/.test(value) && Number(value) > 0;
  };

  const calculateDPMOAndSigma = () => {
    if (!isValidPositiveInteger(opportunities)) {
      alert("Opportunities (O) must be a positive integer.");
      return false;
    }
    if (!isValidPositiveInteger(defects)) {
      alert("Defects (D) must be a non-negative integer.");
      return false;
    }

    const opps = Number(opportunities);
    const defs = Number(defects);

    const calculatedDPMO = (defs / opps) * 1_000_000;
    setDPMO(calculatedDPMO.toFixed(0)); // usually shown as whole number

    const yieldRate = 1 - calculatedDPMO / 1_000_000;
    const z = normalCDFInverse(yieldRate);
    const sigmaLevel = z + 1.5; // classic Six Sigma shift

    setSigma(sigmaLevel.toFixed(2));
    return true;
  };

  const handleShowCard = () => {
    const valid = calculateDPMOAndSigma();
    if (valid) {
      setShowCard(true);
    }
  };

  // const [processDataInput, setProcessDataInput] = useState('');
  // const [usl, setUsl] = useState('');
  // const [lsl, setLsl] = useState('');
  // const [processResults, setProcessResults] = useState(null);

  // const calculateStdDev = (values) => {
  //   if (values.length <= 1) return 0;
  //   const mean = values.reduce((a, b) => a + b, 0) / values.length;
  //   const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / (values.length - 1);
  //   return Math.sqrt(variance);
  // };

  // const calculateProcessCapability = () => {
  //   if (!processDataInput.trim()) {
  //     alert("Please enter some measurement data.");
  //     return;
  //   }
  //   if (!usl || !lsl || Number(usl) <= Number(lsl)) {
  //     alert("Please enter valid USL and LSL (USL > LSL).");
  //     return;
  //   }

  //   const rawValues = processDataInput
  //     .split(',')
  //     .map(v => v.trim())
  //     .filter(v => v !== '' && !isNaN(Number(v)))
  //     .map(Number);

  //   if (rawValues.length < 2) {
  //     alert("Need at least 2 valid numeric values.");
  //     return;
  //   }

  //   const mean = rawValues.reduce((a, b) => a + b, 0) / rawValues.length;
  //   const stdDev = calculateStdDev(rawValues);

  //   const cpu = (Number(usl) - mean) / (3 * stdDev);
  //   const cpl = (mean - Number(lsl)) / (3 * stdDev);
  //   const cpk = Math.min(cpu, cpl);
  //   const cp = (Number(usl) - Number(lsl)) / (6 * stdDev);

  //   // Rough sigma level estimate (many use 3*Cpk as approximation)
  //   const sigmaLevel = 3 * cpk; // or more precise tables if needed

  //   // Prepare line chart data
  //   const chartData = rawValues.map((val, idx) => ({ x: idx + 1, y: val }));

  //   // Simple scaling helper for label positioning (approximate)
  //   const minY = Math.min(...rawValues, Number(lsl));
  //   const maxY = Math.max(...rawValues, Number(usl));
  //   const range = maxY - minY || 1;
  //   const scaledY = (val) => 60 + ((val - minY) / range) * (200); // rough pixel estimate

  //   let warning = "";
  //   if (rawValues.length < 30) {
  //     warning = "Note: For reliable capability indices, use preferably 30+ data points.";
  //   }

  //   setProcessResults({
  //     mean,
  //     stdDev,
  //     cp: isFinite(cp) ? cp : 0,
  //     cpk: isFinite(cpk) ? cpk : 0,
  //     sigmaLevel: isFinite(sigmaLevel) ? sigmaLevel : 0,
  //     chartData,
  //     scaledY,
  //     warning
  //   });
  // };

  const [processDataInput, setProcessDataInput] = useState('');
  const [usl, setUsl] = useState('');
  const [lsl, setLsl] = useState('');
  const [processResults, setProcessResults] = useState(null);

  // Helper: sample standard deviation (n-1)
  const calculateStdDev = (values) => {
    if (values.length <= 1) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / (values.length - 1);
    return Math.sqrt(variance);
  };

  const calculateProcessCapability = () => {
    if (!processDataInput.trim()) {
      alert("Please enter measurement data (comma-separated numbers).");
      return;
    }
    if (!usl || !lsl || Number(usl) <= Number(lsl)) {
      alert("Please enter valid USL and LSL (USL must be greater than LSL).");
      return;
    }

    const rawValues = processDataInput
      .split(',')
      .map(v => v.trim())
      .filter(v => v !== '' && !isNaN(Number(v)))
      .map(Number);

    if (rawValues.length < 5) {  // arbitrary minimum for meaningful stats
      alert("Please enter at least 5 valid numeric values for reliable analysis.");
      return;
    }

    const n = rawValues.length;
    const mean = rawValues.reduce((a, b) => a + b, 0) / n;
    const stdDev = calculateStdDev(rawValues);

    // Capability indices
    const tolerance = Number(usl) - Number(lsl);
    const cp  = tolerance / (6 * stdDev);
    const cpu = (Number(usl) - mean) / (3 * stdDev);
    const cpl = (mean - Number(lsl)) / (3 * stdDev);
    const cpk = Math.min(cpu, cpl);

    // Simple sigma level estimate
    const sigmaLevel = cpk > 0 ? 3 * cpk : 0;

    // Control chart values (Individuals chart – X chart)
    const ucl = mean + 3 * stdDev;
    const lcl = mean - 3 * stdDev;

    // Data prep
    const histogramData = rawValues.map(v => ({ x: v })); // VictoryHistogram expects {x: value}
    const controlData    = rawValues.map((v, i) => ({ x: i + 1, y: v }));

    let warning = "";
    if (n < 30) {
      warning = "Tip: For more reliable capability & control limits, collect 30+ data points.";
    }

    setProcessResults({
      mean: mean,
      stdDev: stdDev,
      cp: isFinite(cp) && cp > 0 ? cp : 0,
      cpk: isFinite(cpk) && cpk > 0 ? cpk : 0,
      sigmaLevel,
      ucl,
      lcl,
      chartData: controlData,
      histogramData,
      n,
      warning
    });
  };

  const [showWhyUsed, setShowWhyUsed] = useState(false); 

  const populateWhyUsed = () => {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Main Card Container */}
        <div className="bg-gradient-to-br from-gray-900 via-black to-gray-950 rounded-2xl shadow-2xl border border-blue-900/40 overflow-hidden">
          
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-blue-900/80 to-purple-900/60 px-8 py-10 text-center border-b border-blue-700/50">
            <h1 className="text-lg lg:text-xl font-bold text-yellow-300 mb-3 flex items-center justify-center gap-3">
              This is for Example: 🎯 Sigma Studio – Your Path to World-Class Flex Printing Quality
            </h1>
            <p className="text-sm lg:text-lg text-gray-200 max-w-3xl mx-auto">
              A powerful, free tool to slash defects, cut waste, and boost profits — no consultants needed!
            </p>
          </div>

          {/* Greeting */}
          <div className="px-4 py-4 text-gray-100 leading-relaxed">
            <p className="text-sm lg:text-lg font-semibold mb-6 text-cyan-300">
              Dear Flex Printing Factory Owners, Quality Team, Floor Managers, Accounts Team, and Production Staff,
            </p>

            <p className="mb-4 text-sm lg:text-lg">
              Our new <span className="font-bold text-teal-400">Process Capability & Six Sigma Calculator</span> tool (inside Sigma Studio) brings world-class quality practices straight to your daily operations — simple, fast, and completely free.
            </p>

            {/* Why Advantages – Card Grid */}
            <div className="mb-4">
              <h2 className="text-sm lg:text-xl font-bold text-yellow-300 mb-6 flex items-center gap-3">
                <span className="text-lg lg:text-xl">💎</span> Why This Tool Will Transform Your Factory
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gray-800/60 backdrop-blur-sm p-6 rounded-xl border border-teal-900/40 hover:border-teal-600/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-900/20">
                  <div className="text-lg lg:text-xl mb-4">📉</div>
                  <h3 className="text-lg lg:text-xl font-semibold text-teal-300 mb-2">40–70% Defect Reduction</h3>
                  <p className="text-gray-300">
                    Real flex packaging plants reduced misregistration & smearing by ~50% using systematic analysis.
                  </p>
                </div>

                <div className="bg-gray-800/60 backdrop-blur-sm p-6 rounded-xl border border-teal-900/40 hover:border-teal-600/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-900/20">
                  <div className="text-lg lg:text-xl mb-4">💰</div>
                  <h3 className="text-lg lg:text-xl font-semibold text-teal-300 mb-2">Lakhs in Annual Savings</h3>
                  <p className="text-gray-300">
                    Less substrate, ink, energy waste + fewer reprints = direct profit impact per machine.
                  </p>
                </div>

                <div className="bg-gray-800/60 backdrop-blur-sm p-6 rounded-xl border border-teal-900/40 hover:border-teal-600/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-900/20">
                  <div className="text-lg lg:text-xl mb-4">📈</div>
                  <h3 className="text-lg lg:text-xl font-semibold text-teal-300 mb-2">Cpk > 1.33 = Predictable Excellence</h3>
                  <p className="text-gray-300">
                    Stable processes → better OEE, consistent first-time-right output, fewer surprises.
                  </p>
                </div>

                <div className="bg-gray-800/60 backdrop-blur-sm p-6 rounded-xl border border-teal-900/40 hover:border-teal-600/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-900/20">
                  <div className="text-lg lg:text-xl mb-4">🧠</div>
                  <h3 className="text-lg lg:text-xl font-semibold text-teal-300 mb-2">Data Beats Guesswork</h3>
                  <p className="text-gray-300">
                    Replace "ink looks off" with real metrics: mean, std dev, histograms & control charts.
                  </p>
                </div>

                <div className="bg-gray-800/60 backdrop-blur-sm p-6 rounded-xl border border-teal-900/40 hover:border-teal-600/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-900/20">
                  <div className="text-lg lg:text-xl mb-4">🤝</div>
                  <h3 className="text-lg lg:text-xl font-semibold text-teal-300 mb-2">Happy FMCG Clients</h3>
                  <p className="text-gray-300">
                    Reliable zero-defect packaging builds long-term trust with demanding brand customers.
                  </p>
                </div>

                <div className="bg-gray-800/60 backdrop-blur-sm p-6 rounded-xl border border-teal-900/40 hover:border-teal-600/60 transition-all duration-300 hover:shadow-lg hover:shadow-teal-900/20">
                  <div className="text-lg lg:text-xl mb-4">🏆</div>
                  <h3 className="text-lg lg:text-xl font-semibold text-teal-300 mb-2">15–30% Efficiency Gains</h3>
                  <p className="text-gray-300">
                    Lean + Six Sigma factories win more business and command higher rates.
                  </p>
                </div>
              </div>
            </div>

            {/* Where & When – Accordion-like cards */}
            <div className="mb-4">
              <h2 className="text-lg lg:text-xl font-bold text-yellow-300 mb-6 flex items-center gap-3">
                <span className="text-lg lg:text-xl">🗺️</span> Where & When to Use It
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-800/50 p-7 rounded-xl border border-purple-900/30">
                  <h3 className="text-sm lg:text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
                    <span>📍</span> Key Areas in Your Factory
                  </h3>
                  <ul className="space-y-3 [&_li]:text-gray-300">
                    <li>• Printing department (70–80% of defects)</li>
                    <li>• Setup / makeready phase</li>
                    <li>• Lamination / slitting / converting</li>
                    <li>• Incoming raw materials check</li>
                    <li>• Daily/weekly production monitoring</li>
                  </ul>
                </div>

                <div className="bg-gray-800/50 p-7 rounded-xl border border-purple-900/30">
                  <h3 className="text-sm lg:text-lg font-semibold text-purple-300 mb-4 flex items-center gap-2">
                    <span>⏰</span> Best Moments to Use It
                  </h3>
                  <ul className="space-y-3 [&_li]:text-gray-300">
                    <li>• Right now – pilot on one problem job</li>
                    <li>• During every job setup</li>
                    <li>• When defects suddenly increase</li>
                    <li>• Monthly/quarterly performance reviews</li>
                    <li>• Before accepting tight-tolerance orders</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How – Numbered Steps with icons */}
            <div className="mb-4">
              <h2 className="text-lg lg:text-xl font-bold text-yellow-300 mb-6 flex items-center gap-3">
                <span className="tetx-lg lg:text-xl">🛠️</span> How to Get Started (Start Small!)
              </h2>

              <div className="space-y-6">
                {[ 
                  { num: 1, icon: "👑", title: "Assign Ownership", text: "Start with Quality Manager as Champion" },
                  { num: 2, icon: "📏", title: "Collect Easy Data", text: "20–50 measurements → comma-separated (e.g. 0.12, 0.08, 0.15)" },
                  { num: 3, icon: "🎯", title: "Enter Specs", text: "USL & LSL from customer or internal standard" },
                  { num: 4, icon: "⚡", title: "Click Calculate", text: "Instant Mean, Std Dev, Cp/Cpk, Histogram, Control Chart" },
                  { num: 5, icon: "🔍", title: "Act on Insights", text: "Fix special causes, reduce variation, update SOPs" },
                  { num: 6, icon: "📈", title: "Scale Up", text: "Expand to more machines after first wins" }
                ].map((step) => (
                  <div key={step.num} className="flex gap-5 items-start bg-gray-800/40 p-5 rounded-xl border border-gray-700/50">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-cyan-600 to-teal-700 flex items-center justify-center text-2xl font-bold text-white">
                      {step.num}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-lg lg:text-xl">{step.icon}</span>
                        <h3 className="text-sm lg:text-lg font-semibold text-teal-300">{step.title}</h3>
                      </div>
                      <p className="text-gray-300">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-2 text-gray-200 italic text-center">
                No big investment — just consistent measurement + 10–15 min per analysis
              </p>
            </div>

            {/* Ownership */}
            <div className="mb-4">
              <h2 className="text-lg lg:text-xl font-bold text-yellow-300 mb-6 flex items-center gap-3">
                <span className="text-lg lg:text-xl">👥</span> Who Should Own This Practice?
              </h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-indigo-950/40 p-6 rounded-xl border border-indigo-800/40">
                  <h3 className="text-sm lg:text-lg font-semibold text-indigo-300 mb-2">Primary Champion</h3>
                  <p className="text-gray-300">Quality Manager / Quality Head</p>
                </div>
                <div className="bg-indigo-950/40 p-6 rounded-xl border border-indigo-800/40">
                  <h3 className="text-sm lg:text-lg font-semibold text-indigo-300 mb-2">Daily Executor</h3>
                  <p className="text-gray-300">Floor Supervisor / Senior Operator</p>
                </div>
                <div className="bg-indigo-950/40 p-6 rounded-xl border border-indigo-800/40">
                  <h3 className="text-sm lg:text-lg font-semibold text-indigo-300 mb-2">Support & Action</h3>
                  <p className="text-gray-300">Floor Manager + Production Manager</p>
                </div>
                <div className="bg-indigo-950/40 p-6 rounded-xl border border-indigo-800/40">
                  <h3 className="text-sm lg:text-lg font-semibold text-indigo-300 mb-2">Oversight & ROI</h3>
                  <p className="text-gray-300">Owner / GM + Accounts/Finance</p>
                </div>
                <div className="bg-indigo-950/40 p-6 rounded-xl border border-indigo-800/40 sm:col-span-2 lg:col-span-1">
                  <h3 className="text-sm lg:text-lg font-semibold text-indigo-300 mb-2">Cross-functional Power Team</h3>
                  <p className="text-gray-300">Quality + Production + Maintenance</p>
                </div>
              </div>
            </div>

            {/* Closing Call to Action */}
            <div className="text-center mt-4 py-8 bg-gradient-to-t from-purple-950/30 to-transparent rounded-2xl">
              <p className="text-lg lg:text-xl font-bold text-teal-300 mb-4">
                This is your proven path from reactive firefighting to predictable, profitable excellence.
              </p>
              <p className="text-sm lg:text-lg text-gray-200 mb-6">
                Let's do a quick 30-minute live demo next week — bring real data from your recent job!
              </p>
              <button 
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white font-bold py-4 px-10 rounded-full text-lg shadow-lg shadow-teal-900/40 transform hover:scale-105 transition-all duration-300"
                onClick={() => alert("Demo scheduling coming soon! Contact Sanjeev.")}
              >
                📅 Schedule Your Free Demo Now
              </button>
            </div>

            <div className="mt-4 text-center text-gray-400">
              <p className="tetx-sm lg:text-lg font-medium">
                Best regards,<br />
                <span className="text-yellow-300">Sanjeev</span> & the Think5Whys Team
              </p>
              <p className="text-xs lg:text-sm mt-2">
                Think5Whys Sigma Studio – 6 Sigma Public Calculator
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const populateExampleData = () => {
    setProcessName("Flexographic Printing – Self Adhesive Label Production");
    setTaskName("Label Print Registration Accuracy Check");
    setTaskDetails(
      "During flexographic printing of pressure-sensitive labels, the print registration must align accurately across multiple colors. Misregistration results in rejected labels or rework."
    );
    setOpportunities(60000);
    setODetails(
      "Each printed label has multiple defect opportunities including color misregistration, ink smudging, cutting tolerance variation, adhesive contamination, and substrate wrinkles."
    );
    setDefects(120);
    setDDetails(
      "Defects observed during inspection include color shift between plates, blurred barcode printing, improper die-cut alignment, and missing print elements."
    );
    setFinalRemarks(
      "Initial analysis indicates moderate process variation. Registration alignment improvement and tighter press calibration may significantly improve Sigma level."
    );
    setProcessDataInput(`50.01, 50.03, 49.98, 50.02, 49.97, 50.05, 50.04, 49.99, 50.00, 50.02, 49.96, 50.01, 49.98, 50.03, 50.00`);
    setUsl("50.10");
    setLsl("49.90");
    setProcessResults(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-gray-100 [&_p]:text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            🎛️ Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">
            6 Sigma DMPO Playground
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">
                {userName}
              </div>
              <button
                onClick={handleLogout}
                className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3">
              <div className="text-teal-300 text-xs lg:text-sm">
                {userName}
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md bg-red-600"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <Link to="/login" className="text-teal-300">
                🔐
              </Link>
            </div>
          )}
        </div>
      )}

      <main className="min-h-[75vh] ml-4 mr-4 mt-4 justify-start">
        <div className="flex flex-row justify-center mb-3">
          <button
            onClick={populateExampleData}
            className="px-3 py-1 text-xs lg:text-sm w-40 lg:w-48 bg-green-600 rounded-md"
          >
            Populate Example Data
          </button>
          <button
            onClick={() => setShowWhyUsed(prev => !prev)}
            className="ml-2 px-3 py-1 text-xs lg:text-sm w-40 lg:w-48 bg-green-600 rounded-md"
          >
            Why It Is Useful (Example)
          </button>          
        </div>
        {showWhyUsed && (
          <div className="mt-2">
              {populateWhyUsed()}
          </div>
        )}
        <div className="flex flex-col">
          <label>Process Name:</label>
          <input
            className="px-2 h-10 text-black"
            type="text"
            style={{width: "75%"}}
            value={processName}
            onChange={(e) => setProcessName(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label>Title:</label>
          <input
            className="px-2 h-10 text-black"
            type="text"
            style={{width: "100%"}}
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
          />
          <button
            className="mt-2 w-32 lg:w-40 text-xs lg:text-sm bg-blue-600 rounded-md"
            onClick={() => setShowTDetails(!showTDetails)}
          >
            Input Details
          </button>
          {showTDetails && (
            <textarea
              className="p-2 mt-4 max-w-full h-40 bg-black text-white"
              value={taskDetails}
              onChange={(e) => setTaskDetails(e.target.value)}
            />
          )}
        </div>        
        <div className="flex flex-col">
          <label>Remarks:</label>
          <input
            className="px-2 h-10 bg-black text-white border border-cyan-100"
            type="text"
            style={{width: "100%"}}
            value={finalRemarks}
            onChange={(e) => setFinalRemarks(e.target.value)}
          />
        </div>
        
        <div className="mt-4 text-xl text-blue-700">
          <h2 className="text-yellow-200">DPMO Calculator</h2>
        </div>

        <div className="mt-4 flex flex-col gap-6">
          <div className="flex flex-row">
            <label>Opportunities (O):</label>
            <input
              className="px-2 w-40 h-10 text-black"
              type="number"
              min="1"
              value={opportunities}
              onChange={(e) => setOpportunities(e.target.value)}
            />
            <button
              className="ml-2 w-32 lg:w-40 text-xs h-10 lg:text-sm bg-blue-600 rounded-md"
              onClick={() => setShowODetails(!showODetails)}
            >
              Input Details
            </button>
          </div>
          {showODetails && (
            <textarea
              className="p-2 mt-4 max-w-full h-40 text-white bg-black"
              value={oDetails}
              onChange={(e) => setODetails(e.target.value)}
            />
          )}          
          <div className="flex flex-row">
            <label>Defects (D):</label>
            <input
              className="px-2 w-40 h-10 text-black"
              type="number"
              min="0"
              value={defects}
              onChange={(e) => setDefects(e.target.value)}
            />
            <button
              className="ml-2 w-32 lg:w-40 h-10 text-xs lg:text-sm bg-blue-600 rounded-md"
              onClick={() => setShowDDetails(!showDDetails)}
            >
              Input Details
            </button>
          </div>
          {showDDetails && (
            <textarea
              className="p-2 mt-4 max-w-full h-40 text-white bg-black"
              value={dDetails}
              onChange={(e) => setDDetails(e.target.value)}
            />
          )}          
          <div className="mb-4">
            <button
              className="mt-2 text-xs lg:text-sm w-64 lg:w-64 bg-purple-800 rounded-md py-2"
              onClick={handleShowCard}
            >
              Show DPMO & 6 Sigma Level Card
            </button>
          </div>

          {showCard && (
            <div
              className="text-white p-5"
              style={{ border: '1px solid #333', marginTop: '2px', borderRadius: '8px' }}
            >
              <h3 className="text-cyan-200 mb-2">6 Sigma Level</h3>
              <p><strong>Process Name:</strong> {processName || '-'}</p>
              <p><strong>Task Name:</strong> {taskName || '-'}</p>
              <h3 className="text-cyan-200 mt-2 mb-2">Data</h3>
              <p>Opportunities: {opportunities}</p>
              <p>Defects: {defects}</p>
              <p>DPMO: {dpmo}</p>

              {/* DPMO Bar Chart – now much larger & responsive */}
              <div className="my-6" style={{ height: "220px", width: "100%" }}>
                <VictoryChart
                  height={220}
                  width={400}           // reasonable default – container constrains it
                  domainPadding={{ x: 60, y: 40 }}
                  padding={{ top: 50, bottom: 50, left: 70, right: 30 }}
                >
                  <VictoryAxis
                    style={{
                      axis: { stroke: "white" },
                      tickLabels: { fill: "white", fontSize: 11 },
                      grid: { stroke: "gray", strokeDasharray: "4,4" }
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    style={{
                      axis: { stroke: "white" },
                      tickLabels: { fill: "white", fontSize: 11 },
                      grid: { stroke: "gray", strokeDasharray: "3,3" }
                    }}
                  />
                  <VictoryBar
                    data={[{ x: "DPMO", y: Number(dpmo) || 0 }]}
                    barWidth={80}
                    labels={({ datum }) => datum.y.toLocaleString()}
                    labelComponent={
                      <VictoryLabel
                        dy={-12}
                        style={{ fill: "white", fontSize: 11, fontWeight: "bold" }}
                      />
                    }
                    style={{ data: { fill: "#f87171" } }}
                  />
                </VictoryChart>
              </div>

              <p className="mt-2"><strong>Sigma Level:</strong> {sigma}</p>

              {/* Sigma Level Indicator */}
              <div className="my-6" style={{ height: "180px", width: "100%" }}>
                <VictoryChart
                  height={180}
                  width={380}
                  domainPadding={{ x: 50, y: 20 }}
                  padding={{ top: 40, bottom: 50, left: 60, right: 30 }}
                >
                  <VictoryAxis
                    style={{
                      axis: { stroke: "white" },
                      tickLabels: { fill: "white", fontSize: 10 }
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    domain={[0, 7]}
                    tickValues={[1, 2, 3, 4, 5, 6]}
                    style={{
                      axis: { stroke: "white" },
                      tickLabels: { fill: "white", fontSize: 11 },
                      grid: { stroke: "gray", strokeDasharray: "4,4" }
                    }}
                  />
                  <VictoryLine
                    style={{ data: { stroke: "#00FFFF", strokeWidth: 3 } }}
                    data={[{ x: "Process", y: Number(sigma) || 0 }]}
                  />
                  <VictoryLabel
                    text={`σ = ${sigma || "?"}`}
                    x={190}
                    y={30}
                    textAnchor="middle"
                    style={{ fill: "white", fontSize: 14, fontWeight: "bold" }}
                  />
                </VictoryChart>
              </div>

              {(showODetails || showDDetails || showTDetails) && (
                <>
                  <h3 className="text-cyan-200 mt-6 mb-2">Details:</h3>
                  {taskDetails   && <p><strong>Description:</strong> {taskDetails}</p>}
                  {oDetails      && <p><strong>Opportunities Remarks:</strong> {oDetails}</p>}
                  {dDetails      && <p><strong>Defects Remarks:</strong> {dDetails}</p>}
                  {finalRemarks  && <p><strong>Final Remarks:</strong> {finalRemarks}</p>}
                </>
              )}
            </div>
          )}

          {/* Advanced Process Capability Calculator */}
          <div
            className="text-white p-2 mt-4"
            style={{ border: '1px solid #333', borderRadius: '8px' }}
          >
            <h2 className="text-yellow-200 text-lg lg:text-xl mb-4">Process Capability Calculator</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Data Input */}
              <div className="flex flex-col">
                <label className="mb-1">Measurements (comma separated):</label>
                <textarea
                  className="h-28 bg-black text-white border border-gray-600 rounded p-2 text-sm"
                  placeholder="e.g. 10.1, 10.3, 9.8, 10.0, 10.2, 9.9, ..."
                  value={processDataInput}
                  onChange={(e) => setProcessDataInput(e.target.value)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Enter your process measurement values
                </p>
              </div>

              {/* Specification Limits */}
              <div className="flex flex-col gap-4">
                <div>
                  <label>Upper Specification Limit (USL):</label>
                  <input
                    className="w-full h-10 mt-1 bg-black text-white border border-gray-600 rounded px-3"
                    type="number"
                    step="any"
                    value={usl}
                    onChange={(e) => setUsl(e.target.value)}
                    placeholder="e.g. 11.0"
                  />
                </div>
                <div>
                  <label>Lower Specification Limit (LSL):</label>
                  <input
                    className="w-full h-10 mt-1 bg-black text-white border border-gray-600 rounded px-3"
                    type="number"
                    step="any"
                    value={lsl}
                    onChange={(e) => setLsl(e.target.value)}
                    placeholder="e.g. 9.0"
                  />
                </div>
              </div>
            </div>

            <button
              className="mt-2 w-48 lg:w-64 text-xs lg:text-sm bg-teal-700 hover:bg-teal-600 py-2 rounded-md font-semibold"
              onClick={calculateProcessCapability}
            >
              Calculate & Analyze
            </button>

            {processResults && (
              <div className="mt-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 text-sm">
                  <div className="bg-gray-900 p-4 rounded">
                    <p className="text-gray-400">Mean</p>
                    <p className="text-xl font-bold">{processResults.mean.toFixed(4)}</p>
                  </div>
                  <div className="bg-gray-900 p-4 rounded">
                    <p className="text-gray-400">Std Dev (sample)</p>
                    <p className="text-xl font-bold">{processResults.stdDev.toFixed(4)}</p>
                  </div>
                  <div className="bg-gray-900 p-4 rounded">
                    <p className="text-gray-400">Cp</p>
                    <p className="text-xl font-bold">{processResults.cp.toFixed(3)}</p>
                  </div>
                  <div className="bg-gray-900 p-4 rounded">
                    <p className="text-gray-400">Cpk</p>
                    <p className="text-xl font-bold">{processResults.cpk.toFixed(3)}</p>
                  </div>
                  <div className="bg-gray-900 p-4 rounded">
                    <p className="text-gray-400">Est. Sigma Level</p>
                    <p className="text-xl font-bold">{processResults.sigmaLevel.toFixed(2)}</p>
                  </div>
                </div>

                {/* Charts – responsive grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 1. Histogram */}
                  <div style={{ height: "320px", width: "100%" }}>
                    <h3 className="text-cyan-300 text-sm mb-2 text-center">Histogram – Process Distribution</h3>
                    <VictoryChart
                      height={320}
                      padding={{ top: 40, bottom: 60, left: 60, right: 40 }}
                      domainPadding={{ x: 20, y: 20 }}
                    >
                      <VictoryAxis
                        label="Measurement Value"
                        style={{
                          axis: { stroke: "#718096" },
                          tickLabels: { fill: "#e2e8f0", fontSize: 10 },
                          axisLabel: { fill: "#a0aec0", fontSize: 12, padding: 35 }
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        label="Frequency"
                        style={{
                          axis: { stroke: "#718096" },
                          tickLabels: { fill: "#e2e8f0", fontSize: 10 },
                          axisLabel: { fill: "#a0aec0", fontSize: 12, angle: -90, padding: 40 }
                        }}
                      />

                      <VictoryHistogram
                        data={processResults.histogramData}
                        bins={Math.min(12, Math.floor(Math.sqrt(processResults.n)) + 2)} // Sturges-ish rule
                        style={{
                          data: { fill: "#63b3ed", stroke: "#2c5282", strokeWidth: 1 }
                        }}
                      />

                      {/* Spec limits as vertical lines */}
                      <VictoryLine
                        data={[{ x: Number(lsl), y: 0 }, { x: Number(lsl), y: 999 }]}
                        style={{ data: { stroke: "#f56565", strokeDasharray: "6,4", strokeWidth: 2 } }}
                      />
                      <VictoryLine
                        data={[{ x: Number(usl), y: 0 }, { x: Number(usl), y: 999 }]}
                        style={{ data: { stroke: "#f56565", strokeDasharray: "6,4", strokeWidth: 2 } }}
                      />

                      <VictoryLabel
                        text={`LSL = ${lsl}`}
                        datum={{ x: Number(lsl), y: 0 }}
                        dx={-10}
                        dy={-10}
                        style={{ fill: "#f56565", fontSize: 10 }}
                      />
                      <VictoryLabel
                        text={`USL = ${usl}`}
                        datum={{ x: Number(usl), y: 0 }}
                        dx={10}
                        dy={-10}
                        style={{ fill: "#f56565", fontSize: 10 }}
                      />
                    </VictoryChart>
                  </div>

                  {/* 2. Control Chart (Individuals) */}
                  <div style={{ height: "320px", width: "100%" }}>
                    <h3 className="text-cyan-300 text-sm mb-2 text-center">Control Chart (Individuals)</h3>
                    <VictoryChart
                      height={320}
                      padding={{ top: 50, bottom: 60, left: 60, right: 40 }}
                      domainPadding={{ y: 30 }}
                    >
                      <VictoryAxis
                        label="Observation #"
                        style={{
                          axis: { stroke: "#718096" },
                          tickLabels: { fill: "#e2e8f0", fontSize: 10 },
                          axisLabel: { fill: "#a0aec0", fontSize: 12, padding: 35 }
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        label="Value"
                        style={{
                          axis: { stroke: "#718096" },
                          tickLabels: { fill: "#e2e8f0", fontSize: 10 },
                          axisLabel: { fill: "#a0aec0", fontSize: 12, angle: -90, padding: 45 }
                        }}
                      />

                      {/* Control limits */}
                      <VictoryLine
                        data={[
                          { x: 1, y: processResults.ucl },
                          { x: processResults.n, y: processResults.ucl }
                        ]}
                        style={{ data: { stroke: "#f56565", strokeDasharray: "5,5" } }}
                      />
                      <VictoryLine
                        data={[
                          { x: 1, y: processResults.mean },
                          { x: processResults.n, y: processResults.mean }
                        ]}
                        style={{ data: { stroke: "#48bb78", strokeWidth: 2 } }}
                      />
                      <VictoryLine
                        data={[
                          { x: 1, y: processResults.lcl },
                          { x: processResults.n, y: processResults.lcl }
                        ]}
                        style={{ data: { stroke: "#f56565", strokeDasharray: "5,5" } }}
                      />

                      {/* Data points */}
                      <VictoryScatter
                        data={processResults.chartData}
                        size={4}
                        style={{ data: { fill: "#63b3ed", stroke: "#2c5282", strokeWidth: 1.5 } }}
                      />

                      {/* Labels */}
                      <VictoryLabel
                        text={`UCL = ${processResults.ucl.toFixed(3)}`}
                        x={70}
                        y={40}
                        style={{ fill: "#f56565", fontSize: 10 }}
                      />
                      <VictoryLabel
                        text={`Mean = ${processResults.mean.toFixed(3)}`}
                        x={70}
                        y={80}
                        style={{ fill: "#48bb78", fontSize: 10 }}
                      />
                      <VictoryLabel
                        text={`LCL = ${processResults.lcl.toFixed(3)}`}
                        x={70}
                        y={120}
                        style={{ fill: "#f56565", fontSize: 10 }}
                      />
                    </VictoryChart>
                  </div>
                </div>
                  
                {/* Charts - responsive by wrapping + aspect ratio */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div style={{ height: "260px", width: "100%" }}>
                    <VictoryChart
                      height={260}
                      padding={{ top: 40, bottom: 60, left: 60, right: 30 }}
                      domainPadding={{ y: 20 }}
                      theme={{
                        axis: {
                          style: {
                            axis: { stroke: "#718096" },
                            tickLabels: { fill: "#e2e8f0", fontSize: 10 },
                            grid: { stroke: "#4a5568", strokeDasharray: "3,3" }
                          }
                        }
                      }}
                    >
                      <VictoryAxis label="Observation" style={{ axisLabel: { fill: "#a0aec0", fontSize: 12 } }} />
                      <VictoryAxis
                        dependentAxis
                        label="Value"
                        style={{ axisLabel: { fill: "#a0aec0", fontSize: 12, angle: -90, padding: 40 } }}
                      />

                      <VictoryLine
                        data={processResults.chartData}
                        style={{ data: { stroke: "#63b3ed", strokeWidth: 2 } }}
                      />

                      {/* LSL & USL lines */}
                      <VictoryLine
                        data={[
                          { x: 1, y: Number(lsl) },
                          { x: processResults.chartData.length, y: Number(lsl) }
                        ]}
                        style={{ data: { stroke: "#f56565", strokeWidth: 2, strokeDasharray: "5,5" } }}
                      />
                      <VictoryLine
                        data={[
                          { x: 1, y: Number(usl) },
                          { x: processResults.chartData.length, y: Number(usl) }
                        ]}
                        style={{ data: { stroke: "#f56565", strokeWidth: 2, strokeDasharray: "5,5" } }}
                      />

                      {/* <VictoryLabel
                        text={`LSL = ${lsl}`}
                        x={40}
                        y={processResults.scaledY(lsl) + 10}
                        style={{ fill: "#f56565", fontSize: 10 }}
                      />
                      <VictoryLabel
                        text={`USL = ${usl}`}
                        x={40}
                        y={processResults.scaledY(usl) - 10}
                        style={{ fill: "#f56565", fontSize: 10 }}
                      />*/}
                      <VictoryLine
                        data={[
                          { x: 1, y: Number(lsl) },
                          { x: processResults.chartData.length, y: Number(lsl) }
                        ]}
                        labels={[`LSL = ${lsl}`]}
                        labelComponent={
                          <VictoryLabel
                            dy={-5}
                            style={{ fill: "#f56565", fontSize: 10 }}
                          />
                        }
                        style={{
                          data: { stroke: "#f56565", strokeWidth: 2, strokeDasharray: "5,5" }
                        }}
                      />

                      <VictoryLine
                        data={[
                          { x: 1, y: Number(usl) },
                          { x: processResults.chartData.length, y: Number(usl) }
                        ]}
                        labels={[`USL = ${usl}`]}
                        labelComponent={
                          <VictoryLabel
                            dy={-5}
                            style={{ fill: "#f56565", fontSize: 10 }}
                          />
                        }
                        style={{
                          data: { stroke: "#f56565", strokeWidth: 2, strokeDasharray: "5,5" }
                        }}
                      />
                    </VictoryChart>                     
                  </div>

                  {/* Simple Cpk Goal Gauge / Bar */}
                  <div style={{ height: "260px", width: "100%" }}>
                    <VictoryChart
                      height={260}
                      padding={{ top: 50, bottom: 60, left: 70, right: 40 }}
                      domainPadding={{ x: 40, y: 20 }}
                      domain={{ y: [0, Math.max(2, processResults.cpk * 1.4 + 0.5)] }}
                    >
                      <VictoryAxis
                        tickValues={[0, 0.67, 1.0, 1.33, 1.67, 2.0]}
                        style={{
                          tickLabels: { fill: "#e2e8f0", fontSize: 10 },
                          grid: { stroke: "#4a5568" }
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        label="Capability"
                        style={{
                          axisLabel: { fill: "#a0aec0", fontSize: 12, padding: 45 },
                          tickLabels: { fill: "#e2e8f0", fontSize: 11 }
                        }}
                      />

                      <VictoryBar
                        horizontal
                        data={[{ x: "Cpk", y: processResults.cpk }]}
                        barWidth={60}
                        style={{ data: { fill: processResults.cpk >= 1.33 ? "#48bb78" : processResults.cpk >= 1.0 ? "#ed8936" : "#e53e3e" } }}
                        labels={({ datum }) => datum.y.toFixed(3)}
                        labelComponent={<VictoryLabel dx={-10} style={{ fill: "white", fontSize: 11, fontWeight: "bold" }} />}
                      />

                      <VictoryLabel
                        text={`Cpk = ${processResults.cpk.toFixed(3)}`}
                        x={130}
                        y={40}
                        textAnchor="middle"
                        style={{ fill: "white", fontSize: 14, fontWeight: "bold" }}
                      />
                    </VictoryChart>
                  </div>
                </div>

                {processResults.warning && (
                  <p className="mt-2 text-orange-400 text-sm">{processResults.warning}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioCalculators;